/*----------------------------------
- DOMAINS MEGA MENU
----------------------------------*/

import CtaPill from '@uniquedomains/components/website/WebsiteCta';
import {
    DOMAINS_MENU_COLUMN_TITLES,
    type LandingHeaderDomainsNavItem,
    type LandingHeaderDomainsNavigation,
} from '@/client/catalogs/landing/pageContent';
import { LANDING_MENU_PANEL_COPY } from '@/client/catalogs/landing/menuPanels';
import { LANDING_WEBSITE_PAGE_ROUTES } from '@/common/catalogs/landing/publicPages';
import MegaMenuPanel from './MegaMenuPanel';
import MenuCountRow from './MenuCountRow';
import MenuPanelHeader from './MenuPanelHeader';
import MenuRow from './MenuRow';
import { NAV_SCREENER_ITEM, type MenuNavigateHandler } from './navClick';
import {
    MENU_SECTION_LABEL_CLASS,
    formatMenuTotal,
    formatTldLabel,
    getDomainsMenuColumnItems,
    readMenuItemCount,
    readMenuTotal,
} from './menuShared';

/*----------------------------------
- TYPES
----------------------------------*/

type DomainsMegaMenuProps = {
    id: string;
    isOpen: boolean;
    menu: LandingHeaderDomainsNavItem;
    navigation: LandingHeaderDomainsNavigation;
    screenerHref: string;
    onNavigate: MenuNavigateHandler;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const COPY = LANDING_MENU_PANEL_COPY.domains;

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * Three plain columns under one headline: the lists a visitor can open, the endings and
 * the sectors, each with the count Product sent. Every figure here is backend data; a
 * figure the payload did not carry drops its sentence rather than printing a zero.
 */
export default function DomainsMegaMenu({
    id,
    isOpen,
    menu,
    navigation,
    screenerHref,
    onNavigate,
}: DomainsMegaMenuProps) {
    const listItems = getDomainsMenuColumnItems(menu, DOMAINS_MENU_COLUMN_TITLES.lists);
    const tldItems = getDomainsMenuColumnItems(menu, DOMAINS_MENU_COLUMN_TITLES.endings);
    const sectorItems = getDomainsMenuColumnItems(menu, DOMAINS_MENU_COLUMN_TITLES.sectors);

    const allCount = readMenuTotal(navigation.totals?.all);
    const tldCount = readMenuTotal(navigation.totals?.tlds);
    const checkedCount = readMenuTotal(navigation.totals?.checkedLast24h);

    return (
        <MegaMenuPanel id={id} testId="domains-mega-menu" isOpen={isOpen}>
            <MenuPanelHeader
                title={allCount !== null ? `${formatMenuTotal(allCount)} ${COPY.titleSuffix}` : COPY.fallbackTitle}
                lead={
                    <>
                        {COPY.lead}
                        {tldCount !== null ? ` Across ${formatMenuTotal(tldCount)} endings.` : ''}
                        {checkedCount !== null
                            ? ` ${formatMenuTotal(checkedCount)} names were rechecked in the last 24 hours.`
                            : ''}
                    </>
                }
                aside={
                    <CtaPill
                        href={screenerHref}
                        onClick={() => onNavigate(NAV_SCREENER_ITEM, screenerHref)}
                        variant="accent"
                        size="sm"
                    >
                        {COPY.screenerCta}
                    </CtaPill>
                }
            />

            <div class="mt-[26px] grid grid-cols-[1.25fr_1fr_1fr] gap-10">
                <section>
                    <div class={MENU_SECTION_LABEL_CLASS}>{DOMAINS_MENU_COLUMN_TITLES.lists}</div>
                    <ul class="mt-2.5 grid grid-cols-2 gap-x-4">
                        {listItems.map((item) => (
                            <li key={item.label}>
                                <MenuRow
                                    href={item.href || screenerHref}
                                    label={item.label}
                                    description={item.description}
                                    iconName={item.iconName}
                                    onClick={() => onNavigate(item.label, item.href || screenerHref)}
                                />
                            </li>
                        ))}
                    </ul>
                </section>

                <section>
                    <div class={MENU_SECTION_LABEL_CLASS}>{DOMAINS_MENU_COLUMN_TITLES.endings}</div>
                    <ul class="mt-2.5 flex flex-col">
                        {tldItems.map((item) => (
                            <li key={item.label}>
                                <MenuCountRow
                                    href={item.href || screenerHref}
                                    label={formatTldLabel(item.label)}
                                    count={readMenuItemCount(item)}
                                    ariaLabel={`Open the ${formatTldLabel(item.label)} page`}
                                    onClick={() => onNavigate(formatTldLabel(item.label), item.href || screenerHref)}
                                />
                            </li>
                        ))}
                    </ul>
                    {/* The endings column shows the public set; the hub is where every TLD page is. */}
                    <a
                        href={LANDING_WEBSITE_PAGE_ROUTES.tldHub}
                        onClick={() => onNavigate(COPY.allTldsLabel, LANDING_WEBSITE_PAGE_ROUTES.tldHub)}
                        class="mt-2 inline-flex text-[14px] font-semibold text-[#1b1d1e] underline underline-offset-4"
                        data-testid="domains-menu-all-tlds"
                    >
                        {COPY.allTldsLabel}
                    </a>
                </section>

                <section>
                    <div class={MENU_SECTION_LABEL_CLASS}>{DOMAINS_MENU_COLUMN_TITLES.sectors}</div>
                    <ul class="mt-2.5 flex flex-col">
                        {sectorItems.map((item) => (
                            <li key={item.label}>
                                <MenuCountRow
                                    href={item.href || screenerHref}
                                    label={item.label}
                                    count={readMenuItemCount(item)}
                                    onClick={() => onNavigate(item.label, item.href || screenerHref)}
                                />
                            </li>
                        ))}
                    </ul>
                </section>
            </div>
        </MegaMenuPanel>
    );
}
